/* Loon'r shared dialog focus-trap (WCAG 2.1.1 / 2.4.3).
   One tiny global helper so every overlay on the site (the PDP image viewer in
   pdp-loonr.js, the hamburger drawer, the spare-liner popup in
   loonr-spare-popup.js, and the cart drawer in snippets/halo-cart-sidebar.liquid)
   traps Tab the same way instead of letting keyboard focus wander onto
   fully-obscured page controls.

   Usage:  var release = window.loonrDialog.trap(containerEl[, opts]);
           ... on close:  release();

   What trap() does:
   - Remembers the currently-focused element and restores it on release.
   - Cycles Tab / Shift+Tab within the container's visible focusables.
   - Sets `inert` on #MainContent while open, but ONLY when the dialog itself
     lives outside #MainContent (the body-appended lightbox, the header drawer,
     the cart drawer). A dialog rendered inside the main content (e.g. the
     cart-page spare popup) skips inert, otherwise it would disable itself.
   - Traps stack (Round 5 P1-8): the spare-liner popup opens ON TOP of the cart
     drawer, so only the most recently opened trap handles Tab, and #MainContent
     stays inert until the last trap that set it is released.
     loonrDialog.isTop(el) says whether el's trap is the topmost one, so an Esc
     handler can leave a dialog stacked above it alone.
   - Steps aside while focus sits inside some other dialog that isn't part of
     this container (a vendor popup such as Klaviyo's), instead of yanking focus
     back out of it.
   - opts.paused (optional function): return true while an overlay that isn't a
     Loon'r trap (e.g. an Ella popup opened from inside the cart drawer) owns
     the keyboard; the trap then lets Tab through.
   - No ES2020+ syntax: this must parse on old iOS Safari (see the `?.` incident,
     PR #506). */
(function () {
  "use strict";
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var OTHER_DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog';
  var stack = []; // open traps, oldest first

  function visibleIn(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        if (el === document.activeElement) return true;
        if (el.offsetParent === null) return false; // display:none
        if (el.getAttribute("tabindex") === "-1") return false; // not a Tab stop
        // visibility:hidden (e.g. a closed popup inside the container) can't take focus
        return window.getComputedStyle(el).visibility !== "hidden";
      }
    );
  }

  function focusInOtherDialog(container) {
    var ae = document.activeElement;
    if (!ae || ae === document.body || ae === document.documentElement) return false;
    if (container.contains(ae) || !ae.closest) return false;
    return !!ae.closest(OTHER_DIALOG);
  }

  function inertStillNeeded() {
    for (var i = 0; i < stack.length; i++) if (stack[i].madeInert) return true;
    return false;
  }

  window.loonrDialog = {
    trap: function (container, opts) {
      opts = opts || {};
      var prevFocus = document.activeElement;
      var main = document.getElementById("MainContent");
      var entry = { container: container, madeInert: false };
      if (main && !main.contains(container) && "inert" in main) {
        main.inert = true;
        entry.madeInert = true;
      }
      stack.push(entry);
      function onKeydown(e) {
        if (e.key !== "Tab") return;
        if (stack[stack.length - 1] !== entry) return; // a dialog stacked on top owns Tab
        if (typeof opts.paused === "function" && opts.paused()) return;
        if (focusInOtherDialog(container)) return;
        var nodes = visibleIn(container);
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        var inside = container.contains(document.activeElement);
        if (e.shiftKey && (document.activeElement === first || !inside)) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
          e.preventDefault(); first.focus();
        }
      }
      document.addEventListener("keydown", onKeydown, true);
      var released = false;
      return function release() {
        if (released) return;
        released = true;
        document.removeEventListener("keydown", onKeydown, true);
        var i = stack.indexOf(entry);
        if (i > -1) stack.splice(i, 1);
        if (entry.madeInert && main && !inertStillNeeded()) main.inert = false;
        if (prevFocus && typeof prevFocus.focus === "function") {
          try { prevFocus.focus(); } catch (err) {}
        }
      };
    },
    isTop: function (container) {
      return stack.length > 0 && stack[stack.length - 1].container === container;
    }
  };
})();
